import { notify } from 'helpers/NotifyHelper';
import { getUserAuthToken } from 'helpers/StorageHelper';
import { t } from 'i18next';
import qs from 'qs';

import http from './axiosConfig';

import type { AxiosError, AxiosResponse } from 'axios';

export interface ApiResponse {
  status: false;
  message: string;
  response?: string | object | null;
  data?: string | object | null;
}
class APIService {
  addHeaders(token?: string | null): void {
    if (token) {
      http.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    } else {
      delete http.defaults.headers.common['Authorization'];
    }
    http.defaults.headers.common['X-Language-Id'] = '1';
    http.defaults.headers.common['ngrok-skip-browser-warning'] = '69420';
  }

  async getData<T, B = Record<string, unknown> | null | object>(
    url: string,
    params?: B
  ): Promise<T> {
    try {
      const token = getUserAuthToken();
      this.addHeaders(token);

      const query = params ? `?${qs.stringify(params)}` : '';
      const res: AxiosResponse<T> = await http.get(`${url}${query}`);

      return res.data;
    } catch (err) {
      const error = err as AxiosError;

      if (error.response?.status !== 401) {
        notify.error(t('something_went_wrong'));
      }

      return Promise.reject(error);
    }
  }

  async submitData<T, B = Record<string, unknown> | null | object>(
    url: string,
    data: B
  ): Promise<T | ApiResponse> {
    try {
      const token = getUserAuthToken();
      this.addHeaders(token);

      const res: any = await http.post(url, data);
      if (!res?.data && res.status !== 200 && res.status !== 201 && res?.message) {
        notify.error(res?.message);
      }
      return res.data;
    } catch (err: any) {
      const error: any = err as AxiosError;
      console.error('submitData', error.response?.data);
      if (error.response?.status !== 401) {
        return {
          status: false,
          message: error?.response?.data?.message || 'Something went wrong!',
        };
      }

      return Promise.reject(error);
    }
  }

  async submitFormDataStream(url: string, data: FormData): Promise<Response> {
    const token = getUserAuthToken() ?? '';
    const baseUrl = (http.defaults.baseURL ?? '').replace(/\/$/, '');

    return fetch(`${baseUrl}${url}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Language-Id': '1',
        'ngrok-skip-browser-warning': '69420',
      },
      body: data,
    });
  }

  async submitFormData<T, B = Record<string, unknown> | null | object>(
    url: string,
    data: B
  ): Promise<T | ApiResponse> {
    try {
      const token = getUserAuthToken();
      this.addHeaders(token);

      const res: AxiosResponse<T> = await http.post(url, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return res.data;
    } catch (err) {
      const error = err as AxiosError;

      if (error.response?.status !== 401) {
        return { status: false, message: t('something_went_wrong') };
      }

      return Promise.reject(error);
    }
  }
  async submitBufferData(
    url: string,
    data: Record<string, unknown> | null | object
  ): Promise<ArrayBuffer | ApiResponse> {
    try {
      const token = getUserAuthToken();
      this.addHeaders(token);

      const res: AxiosResponse<ArrayBuffer> = await http.post(url, data, {
        headers: { 'Content-Type': 'application/json' },
        responseType: 'arraybuffer',
      });

      return res.data;
    } catch (err) {
      const error = err as AxiosError;

      if (error.response?.status !== 401) {
        return { status: false, message: t('something_went_wrong') };
      }

      return Promise.reject(error);
    }
  }

  async submitPatchData<T, B = Record<string, unknown> | null>(
    url: string,
    data: B
  ): Promise<T | ApiResponse> {
    try {
      const token = getUserAuthToken();
      this.addHeaders(token);

      const res: AxiosResponse<T> = await http.patch(url, data);
      return res.data;
    } catch (err) {
      const error = err as AxiosError;

      if (error.response?.status !== 401) {
        return { status: false, message: 'Something went wrong!' };
      }

      return Promise.reject(error);
    }
  }

  async deleteData<T>(
    url: string,
    data?: Record<string, unknown> | null | object
  ): Promise<T | ApiResponse> {
    try {
      const token = getUserAuthToken();
      this.addHeaders(token);

      const res: AxiosResponse<T> = await http.delete(url, { data });
      return res.data;
    } catch (err) {
      const error = err as AxiosError;

      if (error.response?.status !== 401) {
        return { status: false, message: 'Something went wrong!' };
      }

      return Promise.reject(error);
    }
  }

  async getPublicTokenData<T, B = Record<string, unknown> | null>(
    url: string,
    params?: B,
    token = ''
  ): Promise<T | ApiResponse> {
    try {
      this.addHeaders(token);

      const res: AxiosResponse<T> = await http.get(url, { params });
      return res.data;
    } catch (err) {
      const error = err as AxiosError;

      if (error.response?.status !== 401) {
        return { status: false, message: 'Something went wrong!' };
      }

      return Promise.reject(error);
    }
  }

  async submitPublicTokenData<T, B = Record<string, unknown> | null>(
    url: string,
    data: B,
    token = ''
  ): Promise<T | ApiResponse> {
    try {
      this.addHeaders(token);

      const res: AxiosResponse<T> = await http.post(url, data);
      return res.data;
    } catch (err) {
      const error = err as AxiosError;

      if (error.response?.status !== 401) {
        return { status: false, message: 'Something went wrong!' };
      }

      return Promise.reject(error);
    }
  }
}

export default new APIService();
